import React from "react";
import { MobileWindowFrame } from "@shared/ui/overlay/mobile-window-frame/MobileWindowFrame";
import { OverlayDismissBridgeProvider, useOverlayDismissRequest } from "@shared/ui/overlay/overlay-dismiss-bridge";
import type { MobileWindowShellConfig, MobileWindowShellField } from "./types";

function resolveShellField<TProps extends object>(
    value: MobileWindowShellField<TProps> | undefined,
    props: TProps,
): React.ReactNode | undefined {
    if (value == null) {
        return undefined;
    }

    return typeof value === "function" ? value(props) : value;
}

interface MobileWindowShellFrameProps<TProps extends object> {
    Content: React.ComponentType<TProps>;
    shell: MobileWindowShellConfig<TProps>;
    props: TProps;
}

function MobileWindowShellFrame<TProps extends object>({ Content, shell, props }: MobileWindowShellFrameProps<TProps>) {
    const requestDismiss = useOverlayDismissRequest();

    return (
        <MobileWindowFrame
            title={resolveShellField(shell.title, props)}
            supportingTitle={resolveShellField(shell.supportingTitle, props)}
            footer={resolveShellField(shell.footer, props)}
            className={shell.className}
            contentClassName={shell.contentClassName}
            footerClassName={shell.footerClassName}
            onRequestClose={requestDismiss}
        >
            <Content {...props} />
        </MobileWindowFrame>
    );
}

/**
 * Оборачивает content-компонент в {@link MobileWindowFrame} для mobile window overlay.
 */
export function withMobileWindowShell<TProps extends object>(
    Content: React.ComponentType<TProps>,
    shell: MobileWindowShellConfig<TProps>,
): React.ComponentType<TProps> {
    function MobileWindowContent(props: TProps) {
        return (
            <OverlayDismissBridgeProvider>
                <MobileWindowShellFrame Content={Content} shell={shell} props={props} />
            </OverlayDismissBridgeProvider>
        );
    }

    MobileWindowContent.displayName = `MobileWindow(${Content.displayName ?? Content.name ?? "Content"})`;

    return MobileWindowContent;
}
